import React, { FC, useEffect, useState } from "react";
import { Button, makeStyles, Subtitle2 } from "@fluentui/react-components";
import { CalendarDateRegular, OpenRegular } from "@fluentui/react-icons";
import { supabaseApi } from "../../../../services";
import { Space, SpaceMeeting } from "../../../../models";
import { useAddInStore } from "../../../../hooks";
import { InformationRow } from "./InformationRow";
import { getCADchatSpaceLink } from "../../../../utils/helpers";
import { SpaceAttendeeSelectList } from "../../common";
import { logger } from "../../../../utils";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    padding: "0 15px",
    gap: "5px",
  },
  section_header: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    marginBottom: "10px",
    alignContent: "center",
    alignItems: "center",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    marginBottom: "5px",
    alignContent: "center",
    alignItems: "center",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    marginTop: "10px",
  },
  member_list: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "10px",
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
  },
  body_lg: {
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
  },
});

export const CADchatMeetingDetails: FC = () => {
  // used to control component styles
  const styles = useStyles();

  // used to set the current space
  const meeting = useAddInStore<SpaceMeeting | undefined>((state) => state.meeting);

  // indicates the space associated with the meeting
  const [space, setSpace] = useState<Space | undefined>(undefined);

  // indicates the current time
  const now = new Date();

  // indicates the meeting start time
  const startDate = meeting?.start_date ? new Date(meeting.start_date) : null;

  // indicates the meeting end time
  const endDate = meeting?.end_date ? new Date(meeting.end_date) : null;

  // indicates that the meeting wil start in the next to minutes
  const willMeetingStartSoon =
    startDate && now < startDate && startDate.getTime() - now.getTime() <= 10 * 60 * 1000;

  // indicates that the meeting is already in progress
  const isMeetingInProgress = startDate && endDate && now > startDate && now < endDate;

  useEffect(() => {
    const fetchSpace = async () => {
      try {
        if (meeting?.space_id) {
          const space = await supabaseApi.getSpace(meeting.space_id);
          setSpace(space);
        }
      } catch (error) {
        setSpace(undefined);
        logger.error("Error fetching space details", error);
      }
    };

    fetchSpace();
  }, [meeting?.space_id]);

  return meeting ? (
    <section className={styles.section}>
      <div className={styles.column}>
        <div className={styles.section_header}>
          <CalendarDateRegular />
          <Subtitle2>CADchat Meeting</Subtitle2>
        </div>
        {space ? (
          <InformationRow label="Space Name:" type="text" value={space?.name ?? ""} />
        ) : null}
        <InformationRow label="Created By:" type="user_profile" value={meeting.created_by} />
      </div>

      <div className={styles.member_list}>
        <SpaceAttendeeSelectList
          spaceId={meeting?.space_id}
          meetingCreatedBy={meeting?.created_by}
        />
      </div>

      <div className={styles.row}>
        <Button
          // as="a"
          appearance="primary"
          icon={<OpenRegular />}
          style={{ flex: "1 auto" }}
          // href={getCADchatSpaceLink(meeting.space_id, willMeetingStartSoon || isMeetingInProgress)}
          onClick={() => {
            try {
              Office.context.ui.openBrowserWindow(
                getCADchatSpaceLink(meeting.space_id, willMeetingStartSoon || isMeetingInProgress)
              );
            } catch (error) {
              logger.error("Error opening CADchat space link", error);
              // Fallback to opening in a new tab if the dialog fails
              window.open(
                getCADchatSpaceLink(meeting.space_id, willMeetingStartSoon || isMeetingInProgress),
                "_blank"
              );
            }
          }}
          target="_blank"
        >
          {willMeetingStartSoon || isMeetingInProgress ? "Join Meeting" : "View Workspace"}
        </Button>
      </div>
    </section>
  ) : null;
};
